import type { SpeechMode, SpeechStopReason, UseSpeechRecordingOptions, UseSpeechRecordingReturn } from './const';
import type { RecordingState } from '@/services/speech';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SpeechErrorCode as ErrorCode, MAX_RECORDING_DURATION, PERMISSION_TIMEOUT, transcribeSpeech } from '@/services/speech';
import {
  buildTextPreview,
  collectSpeechRecordCancel,
  collectSpeechRecordStart,
  collectSpeechRecordStop,
  collectSpeechTranscribeFail,
  collectSpeechTranscribeStart,
  collectSpeechTranscribeSuccess,
  generateSpeechTraceId,
} from './collect';
import { COUNTDOWN_THRESHOLD } from './const';
import { formatDuration, getPreferredAudioDeviceId, getSupportedMimeType, isRecordingSupported } from './helper';

export type {
  SpeechMode,
  SpeechStartSource,
  SpeechStopReason,
  SpeechTranscribedMeta,
  UseSpeechRecordingOptions,
  UseSpeechRecordingReturn,
} from './const';
export { COUNTDOWN_THRESHOLD } from './const';

/**
 * 语音录制 Hook
 *
 * @example
 * ```tsx
 * const {
 *   state,
 *   formattedDuration,
 *   isRecording,
 *   startRecording,
 *   stopRecording,
 *   analyser,
 * } = useSpeechRecording({
 *   onTranscribed: (text, meta) => {
 *     editor.commands.insertContent(text);
 *     // meta: { speechTraceId, transcribeId, transcribeTextPreview }
 *   },
 *   onError: (code) => {
 *     // 业务侧根据错误码映射文案
 *     Message.error(code);
 *   },
 * });
 * ```
 */
export function useSpeechRecording(options: UseSpeechRecordingOptions): UseSpeechRecordingReturn {
  const { maxDuration = MAX_RECORDING_DURATION, onTranscribed, onError, onMaxDuration } = options;

  const [state, setState] = useState<RecordingState>('idle');
  const [mode, setMode] = useState<SpeechMode>(null);
  const [duration, setDuration] = useState(0);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);

  // Refs for cleanup
  const speechTraceIdRef = useRef<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const mimeTypeRef = useRef<string>('audio/webm');
  const isRequestingPermissionRef = useRef<boolean>(false);
  const durationRef = useRef<number>(0);

  // Refs for callbacks to avoid stale closures in setInterval
  const onMaxDurationRef = useRef(onMaxDuration);
  onMaxDurationRef.current = onMaxDuration;

  /**
   * 清理所有资源
   */
  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    recorderRef.current = null;
    chunksRef.current = [];
    isRequestingPermissionRef.current = false;
    setAnalyser(null);
  }, []);

  /**
   * 设置麦克风和音频分析器（带超时机制）
   */
  const setupMicrophone = useCallback(async () => {
    // 获取首选的本地麦克风设备（过滤掉 iPhone 等远程设备）
    const preferredDeviceId = await getPreferredAudioDeviceId();

    const getUserMediaWithTimeout = (deviceId?: string): Promise<MediaStream> => {
      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('PermissionTimeout'));
        }, PERMISSION_TIMEOUT);

        const audioConstraints: MediaTrackConstraints = {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        };

        // 如果有首选设备，指定设备 ID
        if (deviceId) {
          audioConstraints.deviceId = { exact: deviceId };
        }

        navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
        }).then((stream) => {
          clearTimeout(timeoutId);
          resolve(stream);
        }).catch((error) => {
          clearTimeout(timeoutId);
          reject(error);
        });
      });
    };

    let stream: MediaStream;
    try {
      // 优先使用本地麦克风
      stream = await getUserMediaWithTimeout(preferredDeviceId);
    } catch (error) {
      // 如果指定设备失败，回退到默认设备
      if (preferredDeviceId) {
        stream = await getUserMediaWithTimeout();
      } else {
        throw error;
      }
    }
    streamRef.current = stream;

    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;

    const source = audioContext.createMediaStreamSource(stream);
    const analyserNode = audioContext.createAnalyser();
    analyserNode.fftSize = 256;
    source.connect(analyserNode);

    setAnalyser(analyserNode);

    return { stream, analyser: analyserNode };
  }, []);

  /**
   * 检查麦克风权限状态
   */
  const checkPermissionStatus = useCallback(async (): Promise<'granted' | 'denied' | 'prompt'> => {
    try {
      if (!navigator.permissions?.query) {
        return 'prompt';
      }
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      return result.state;
    } catch {
      return 'prompt';
    }
  }, []);

  /**
   * 停止录音并转写
   *
   * 埋点约束：
   * - `chat_speech_transcribe_success` 只有在调用方插入输入框成功后才会上报（由 `onTranscribed` 是否 throw 决定）
   * - `speech_trace_id` 用于串联同一次语音尝试的多条事件（record/transcribe/post_edit）
   */
  const stopRecording = useCallback(async (stopReason?: SpeechStopReason) => {
    if (state !== 'recording' || !recorderRef.current) {
      return;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setState('processing');

    const audioBlob = await new Promise<Blob>((resolve) => {
      const recorder = recorderRef.current!;

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
        resolve(blob);
      };

      recorder.stop();
    });

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    setAnalyser(null);

    const recordedSeconds = durationRef.current;
    const audioSizeBytes = audioBlob.size;
    const mimeType = mimeTypeRef.current || audioBlob.type || 'audio/webm';
    const isEmptyAudio = audioSizeBytes === 0;

    const speechTraceId = speechTraceIdRef.current;
    const speechMode = mode;
    const finalStopReason: SpeechStopReason = stopReason ?? (speechMode === 'pushToTalk' ? 'key_up' : 'confirm');
    if (speechTraceId && speechMode) {
      collectSpeechRecordStop({
        speechTraceId,
        speechMode,
        stopReason: finalStopReason,
        recordedSeconds,
        isEmptyAudio,
        audioSizeBytes,
        mimeType,
      });
    }

    if (isEmptyAudio) {
      setState('idle');
      setMode(null);
      setDuration(0);
      durationRef.current = 0;
      speechTraceIdRef.current = null;
      return;
    }

    const safeSpeechTraceId = speechTraceId ?? generateSpeechTraceId();

    const transcribeStartAt = performance.now();
    abortControllerRef.current = new AbortController();
    collectSpeechTranscribeStart({
      speechTraceId: safeSpeechTraceId,
      recordedSeconds,
      audioSizeBytes,
      mimeType,
    });

    try {
      const result = await transcribeSpeech(audioBlob, abortControllerRef.current.signal);
      const latencyMs = Math.round(performance.now() - transcribeStartAt);

      if (!result.text) {
        onError?.(ErrorCode.TRANSCRIPTION_ERROR);
        collectSpeechTranscribeFail({
          speechTraceId: safeSpeechTraceId,
          errorCode: ErrorCode.TRANSCRIPTION_ERROR,
          latencyMs,
        });
        return;
      }

      const transcribeTextPreview = buildTextPreview(result.text);

      try {
        await onTranscribed(result.text, {
          speechTraceId: safeSpeechTraceId,
          transcribeId: result.id,
          transcribeTextPreview,
        });
      } catch {
        onError?.(ErrorCode.TRANSCRIPTION_ERROR);
        collectSpeechTranscribeFail({
          speechTraceId: safeSpeechTraceId,
          errorCode: ErrorCode.TRANSCRIPTION_ERROR,
          latencyMs,
        });
        return;
      }

      collectSpeechTranscribeSuccess({
        speechTraceId: safeSpeechTraceId,
        transcribeId: result.id,
        latencyMs,
        transcribeTextPreview,
      });
    } catch (error) {
      const err = error as Error;

      if (err.name === 'AbortError') {
        // 用户取消，不报错
        return;
      }

      const latencyMs = Math.round(performance.now() - transcribeStartAt);
      const errorCode = (() => {
        if (err.name === 'TimeoutError') {
          return ErrorCode.TIMEOUT_ERROR;
        }
        const anyError = err as unknown as { code?: string; message?: string };
        const message = anyError.message ?? err.message;
        if (anyError.code === 'ERR_NETWORK' || message === 'Network Error') {
          return ErrorCode.NETWORK_ERROR;
        }
        return ErrorCode.TRANSCRIPTION_ERROR;
      })();

      onError?.(errorCode);
      collectSpeechTranscribeFail({
        speechTraceId: safeSpeechTraceId,
        errorCode,
        latencyMs,
      });
    } finally {
      setState('idle');
      setMode(null);
      setDuration(0);
      durationRef.current = 0;
      abortControllerRef.current = null;
      speechTraceIdRef.current = null;
    }
  }, [state, mode, onTranscribed, onError]);

  /**
   * 内部启动录音逻辑
   */
  const startRecordingInternal = useCallback(async (initialMode: SpeechMode) => {
    if (state !== 'idle' || isRequestingPermissionRef.current) {
      return;
    }

    if (!isRecordingSupported()) {
      onError?.(ErrorCode.NOT_SUPPORTED);
      return;
    }

    const permissionStatus = await checkPermissionStatus();
    if (permissionStatus === 'denied') {
      onError?.(ErrorCode.PERMISSION_DENIED);
      return;
    }

    try {
      isRequestingPermissionRef.current = true;
      const { stream } = await setupMicrophone();
      isRequestingPermissionRef.current = false;

      mimeTypeRef.current = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, { mimeType: mimeTypeRef.current });
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.start(1000);

      const speechTraceId = generateSpeechTraceId();
      speechTraceIdRef.current = speechTraceId;
      collectSpeechRecordStart({
        speechTraceId,
        speechMode: initialMode,
        startSource: initialMode === 'pushToTalk' ? 'hotkey' : 'button',
        mimeType: mimeTypeRef.current,
      });

      setState('recording');
      setMode(initialMode);
      setDuration(0);
      durationRef.current = 0;

      timerRef.current = window.setInterval(() => {
        durationRef.current += 1;
        setDuration(durationRef.current);
      }, 1000);
    } catch (error) {
      cleanup();
      const err = error as Error;

      if (err.message === 'PermissionTimeout') {
        onError?.(ErrorCode.PERMISSION_DISMISSED);
      } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        onError?.(ErrorCode.PERMISSION_DENIED);
      } else {
        onError?.(ErrorCode.NETWORK_ERROR);
      }
    }
  }, [state, checkPermissionStatus, setupMicrophone, cleanup, onError]);

  /** 开始正常录音 */
  const startRecording = useCallback(() => startRecordingInternal('normal'), [startRecordingInternal]);

  /** 开始快速录入 */
  const startPushToTalk = useCallback(() => startRecordingInternal('pushToTalk'), [startRecordingInternal]);

  /** 取消录音 */
  const cancelRecording = useCallback(() => {
    const speechTraceId = speechTraceIdRef.current;
    if (state === 'recording' && speechTraceId && mode) {
      collectSpeechRecordCancel({
        speechTraceId,
        speechMode: mode,
        recordedSeconds: durationRef.current,
      });
    }

    cleanup();
    setState('idle');
    setMode(null);
    setDuration(0);
    durationRef.current = 0;
    speechTraceIdRef.current = null;
  }, [cleanup, mode, state]);

  /** 切换到正常模式（录音继续） */
  const switchToNormalMode = useCallback(() => {
    if (state === 'recording' && mode === 'pushToTalk') {
      setMode('normal');
    }
  }, [state, mode]);

  /** 结束快速录入 */
  const stopPushToTalk = useCallback(async (stopReason?: 'key_up' | 'blur') => {
    await stopRecording(stopReason ?? 'key_up');
  }, [stopRecording]);

  // 监听录音时长，达到最大时长时自动停止
  useEffect(() => {
    if (state === 'recording' && duration >= maxDuration) {
      stopRecording('max_duration');
      onMaxDurationRef.current?.();
    }
  }, [state, duration, maxDuration, stopRecording]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // 计算剩余时间和倒计时状态
  const remainingSeconds = useMemo(() => {
    return Math.max(0, maxDuration - duration);
  }, [maxDuration, duration]);

  const isCountingDown = useMemo(() => {
    return state === 'recording' && remainingSeconds <= COUNTDOWN_THRESHOLD;
  }, [state, remainingSeconds]);

  return {
    state,
    mode,
    duration,
    formattedDuration: formatDuration(duration),
    isRecording: state === 'recording',
    isProcessing: state === 'processing',
    isActive: state !== 'idle',
    startRecording,
    stopRecording,
    cancelRecording,
    startPushToTalk,
    stopPushToTalk,
    switchToNormalMode,
    analyser,
    remainingSeconds,
    isCountingDown,
  };
}
